import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/Input";
import { WEBSITE_SCHEME as SCHEME } from "@/utils/website";

/**
 * RHFInput Component
 * A reusable input component integrated with React Hook Form.
 *
 * @param {string} name - The name of the input field (used for form data).
 * @param {string} label - The label to be displayed above the input field.
 * @param {string} placeholder - The placeholder text for the input.
 * @param {string} [type='text'] - The type of the input field (e.g., 'text', 'password').
 * @param {Object} rules - Validation rules for React Hook Form.
 * @param {string} [className=''] - Additional custom classes for the wrapper div.
 * @param {string} [inputClass=''] - Additional custom classes for the input element.
 * @param {boolean} [required=false] - Whether the field is mandatory (shows * in label).
 * @param {string} [prefix] - Fixed text before the box that the user cannot edit
 *   (e.g. "https://"). The form holds prefix + what was typed; a pasted value
 *   that already starts with a scheme is not doubled, and an empty box holds "".
 *
 * @returns {JSX.Element} The rendered input field with validation.
 */

const RHFInput = ({ name, label, placeholder, type = "text", rules, className = "", inputClass = "", maxLength, maxLengthMessage, required = false,labelClass="" , prefix, ...props}) => {
  const { control, formState, getFieldState, setError, clearErrors } = useFormContext();

  const { error } = getFieldState(name, formState);

  return (
    <div className={className}>
      {label && (
        <label className={cn("block text-sm mb-1.5 text-text-secondary", labelClass)} htmlFor={name}>
          {label}
          {required && <span className="text-red-500 font-bold text-[16px] ml-1">*</span>}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        rules={rules}
        defaultValue=""
        render={({ field }) => {
          const input = (
          <Input
            {...field}
            {...props}
            value={prefix ? String(field.value ?? "").replace(SCHEME, "") : (field.value ?? "")}
            type={type}
            id={name}
            placeholder={placeholder}
            onChange={(e) => {
              const typed = prefix ? e.target.value.trim().replace(SCHEME, "") : e.target.value;
              const nextValue = prefix ? (typed ? `${prefix}${typed}` : "") : typed;

              if (maxLength && nextValue.length > maxLength) {
                setError(name, {
                  type: "manual",
                  message: maxLengthMessage || `Must be ${maxLength} characters or fewer`,
                });
                return;
              }

              clearErrors(name);
              field.onChange(nextValue);
              props.onChange?.(e); // ← add this line
            }}
            // The background comes from `Input` itself (`bg-background-form/40`),
            // which is why none is set here. A stray `<bg-background-prima>`
            // tag used to sit in this string — tailwind-merge does not
            // recognise it, so it resolved no conflict and simply rode into
            // every input's class attribute as literal markup.
            className={cn(
              "w-full rounded-md px-4 py-2.5 text-sm text-text-primary focus:outline-none border",
              error ? "border-status-error" : "border-border-input",
              prefix && "rounded-l-none",
              inputClass,
            )}
          />
          );
          if (!prefix) return input;
          return (
            <div className="flex items-stretch">
              <span
                className={cn(
                  "inline-flex shrink-0 select-none items-center rounded-l-md border border-r-0 bg-background-tertiary px-3 text-sm text-text-secondary",
                  error ? "border-status-error" : "border-border-input",
                )}
              >
                {prefix}
              </span>
              {input}
            </div>
          );
        }}
      />

      {error && <p className="text-red-500 text-xs mt-1">{error.message}</p>}
    </div>
  );
};

export default RHFInput;
